import { pricingMaster, stayData } from "../../../../services/packageService";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle, useCallback } from "react";
import Toaster from "../../../common/Toster";
import StayCategoryTabs from "../pricing/components/StayCategoryTabs";
import BasePriceSection from "../pricing/components/BasePriceSection";
import SeasonalPricingSection from "../pricing/components/SeasonalPricingSection";
import SeasonalRatePanel from "../pricing/components/SeasonalRatePanel";
import StopSaleSection from "../pricing/components/StopSaleSection";
import PaymentRulesSection from "../pricing/components/PaymentRulesSection";
import AdvancedPricingAccordion from "../pricing/components/AdvancedPricingAccordion";
import {
  initialRateState,
  WEEKDAYS,
  DEFAULT_ADMIN_FEE,
} from "../pricing/utils/pricingDefaults";
import {
  calculateNetReceive,
  parseAmount,
} from "../pricing/utils/pricingCalculations";
import { formatPricingDate } from "../pricing/utils/formatDate";
import {
  addRates,
  getRecentlyAddedRates,
  getExistingPricingData,
  insertBasePrice,
  getBasePrice,
  updateBasePrice,
  addPackagePriceStopSales,
  listPackagePriceStopSales,
  getDiscountAndPaymentData,
  deleteRate,
  updateRate,
  deleteStopSale,
  // updateStopSale,
} from "../../../../services/packageService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isValidAdminCharge } from "../../../../constants/adminCharge";

const validateBasePriceFields = (currentBasePrice) => {
  const errors = {};
  if (
    !currentBasePrice.adult?.basePrice ||
    parseAmount(currentBasePrice.adult.basePrice) <= 0
  ) {
    errors.adult = { ...errors.adult, basePrice: true };
  }
  if (!currentBasePrice.adult?.adminFee) {
    errors.adult = { ...errors.adult, adminFee: true };
  }
  if (
    !currentBasePrice.child?.basePrice ||
    parseAmount(currentBasePrice.child.basePrice) <= 0
  ) {
    errors.child = { ...errors.child, basePrice: true };
  }
  if (!currentBasePrice.child?.adminFee) {
    errors.child = { ...errors.child, adminFee: true };
  }
  return errors;
};

const normalizeOptionalAdminFee = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  return Number(value) === 0 ? "" : value;
};

const sanitizePriceInput = (value) => String(value).replace(/[^\d.]/g, "");

const isValidPartialPriceInput = (value) =>
  value === "" || /^\d*\.?\d*$/.test(value);

const Pricing = forwardRef(function Pricing(
  { packageid, setPricingData, onPinSection, onUserEdit },
  ref
) {
  const [showToaster, setShowToaster] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const queryClient = useQueryClient();
  const [showRateDialouge, setShowRateDialouge] = useState(false);
  const pricingDataUpdateRef = useRef(null); // Track last pricing data update to prevent infinite loops
  const dirtyBasePriceCategoriesRef = useRef(new Set());
  const stablePricingCategoriesRef = useRef([]);

  const notifyUserEdit = () => onUserEdit?.();
  const [discount, setDiscount] = useState(10);
  const [partialAmount, setPartialAmount] = useState("");
  const [paymentDays, setPaymentDays] = useState("");
  const [paymentMode, setPaymentMode] = useState("full");
  const [globalAdminFee, setGlobalAdminFee] = useState(DEFAULT_ADMIN_FEE);

  // Edit/Delete state management
  const [isEditing, setIsEditing] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  // const [editingStopSale, setEditingStopSale] = useState(null);

  const handleCategorySelect = (id) => {
    setExpandedId(id);
  };

  const [rateTypesByAccordion, setRateTypesByAccordion] = useState({});
  const [startingAtBasePriceByAccordion, setStartingAtBasePriceByAccordion] =
    useState({});
  const [choosedDatesByAccordion, setChoosedDatesByAccordion] = useState({});
  const [stopSaleDatesByAccordion, setStopSaleDatesByAccordion] = useState({});

  const [rateValues, setRateValues] = useState(initialRateState);

  const [savedRatesAsPerAccordion, setSavedRatesAsPerAccordion] = useState([]);
  const [stopSalesData, setStopSalesData] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [stopSaleSelectedDays, setStopSaleSelectedDays] = useState([]);

  // is base price setted
  const [isBasePriceSettedByAccordion, setIsBasePriceSettedByAccordion] =
    useState({});
  const [basePriceByAccordion, setBasePriceByAccordion] = useState({});

  // Add this after the state declarations at the top
  const [basePriceErrors, setBasePriceErrors] = useState({});

  const { data: discountAndPaymentData } = useQuery({
    queryKey: ["discountAndPaymentData", packageid],
    queryFn: () => getDiscountAndPaymentData(packageid),
    enabled: !!packageid,
  });

  const { data: pricingMasterData } = useQuery({
    queryKey: ["pricingMaster", packageid],
    queryFn: () => pricingMaster(packageid),
    enabled: !!packageid,
    placeholderData: keepPreviousData,
  });

  // Fetch package stay data to filter pricing by enabled stay categories
  const { data: packageStayData } = useQuery({
    queryKey: ["packageStay", packageid],
    queryFn: () => stayData(packageid),
    enabled: !!packageid,
    placeholderData: keepPreviousData,
  });

  // Filter pricing data to only show categories that have active stays.
  // Keep the last valid list during refetches so stay tabs do not disappear.
  const pricingData = useMemo(() => {
    const masterList = pricingMasterData?.data;
    const stayList = packageStayData?.data;

    if (!masterList?.length) {
      return stablePricingCategoriesRef.current.length
        ? stablePricingCategoriesRef.current
        : null;
    }

    if (!stayList?.length) {
      return stablePricingCategoriesRef.current.length
        ? stablePricingCategoriesRef.current
        : null;
    }

    const activeStayCategoryIds = new Set(
      stayList.map((stay) => stay.stay_category_id)
    );
    const filtered = masterList.filter((item) =>
      activeStayCategoryIds.has(item.stay_category_id)
    );

    if (filtered.length > 0) {
      stablePricingCategoriesRef.current = filtered;
      return filtered;
    }

    return stablePricingCategoriesRef.current.length
      ? stablePricingCategoriesRef.current
      : null;
  }, [pricingMasterData?.data, packageStayData?.data]);

  const { data: getPricingDataResponse } = useQuery({
    queryKey: [`getExistingPricingData-${expandedId}-${packageid}`],
    queryFn: () => getExistingPricingData(packageid, expandedId),
    enabled: !!expandedId,
  });

  // Query to fetch all rates for all accordion items to calculate starting prices
  const { data: allRatesResponse } = useQuery({
    queryKey: [`getAllRatesForStartingPrice-${packageid}`],
    queryFn: async () => {
      if (!pricingData) return null;

      const allRatesPromises = pricingData.map((item) =>
        getExistingPricingData(packageid, item.id)
      );

      const results = await Promise.all(allRatesPromises);

      // Create a map of accordion id to rates data
      const allRatesMap = {};
      results.forEach((result, index) => {
        if (result && result.data) {
          allRatesMap[pricingData[index].id] = result.data;
        }
      });

      return allRatesMap;
    },
    enabled: !!packageid && !!pricingData,
  });

  const { data: recentlyAddedRates } = useQuery({
    queryKey: [
      `recentlyAddedRates-${rateTypesByAccordion[expandedId]}-${expandedId}`,
    ],
    queryFn: () =>
      getRecentlyAddedRates(
        rateTypesByAccordion[expandedId],
        packageid,
        pricingData?.find((item) => item.id === expandedId)?.stay_category_id,
        expandedId
      ),
    enabled:
      !!expandedId &&
      !!rateTypesByAccordion[expandedId] &&
      !!pricingData &&
      rateTypesByAccordion[expandedId] !== "stop",
  });

  // insert base price mutation — removed; base prices save via Save Pricing footer

  // get base price mutation
  const { data: basePriceData } = useQuery({
    queryKey: [`getBasePrice-${packageid}-${expandedId}`],
    queryFn: () =>
      getBasePrice({
        package_id: packageid,
        package_stay_category_id: expandedId,
      }),
    enabled: !!expandedId && !!packageid,
  });

  // get all base prices for all accordion items
  const { data: allBasePricesData } = useQuery({
    queryKey: [`getAllBasePrices-${packageid}`],
    queryFn: async () => {
      if (!pricingData) return null;

      const basePricePromises = pricingData.map((item) =>
        getBasePrice({
          package_id: packageid,
          package_stay_category_id: item.id,
        })
      );

      const results = await Promise.all(basePricePromises);

      // Create a map of accordion id to base price data
      const basePriceMap = {};
      results.forEach((result, index) => {
        if (result && result.data && result.data.length > 0) {
          basePriceMap[pricingData[index].id] = result.data[0];
        }
      });

      return basePriceMap;
    },
    enabled: !!packageid && !!pricingData,
  });

  // update base price — removed; base prices save via Save Pricing footer

  // list package price stop sales mutation
  const { data: listPackagePriceStopSalesData } = useQuery({
    queryKey: [`listPackagePriceStopSales-${packageid}-${expandedId}`],
    queryFn: () => listPackagePriceStopSales(packageid, expandedId),
    enabled: !!expandedId && !!packageid,
  });

  //  baseprice populating for expanded accordion
  useEffect(() => {
    if (basePriceData && expandedId) {
      if (dirtyBasePriceCategoriesRef.current.has(expandedId)) return;

      const hasBasePrice =
        basePriceData.data[0] &&
        (basePriceData.data[0].adult_price ||
          basePriceData.data[0].child_price ||
          basePriceData.data[0].infant_price);

      setIsBasePriceSettedByAccordion((prev) => ({
        ...prev,
        [expandedId]: hasBasePrice,
      }));

      setBasePriceByAccordion((prev) => ({
        ...prev,
        [expandedId]: {
          id: basePriceData.data[0]?.id || "",
          adult: {
            basePrice: basePriceData.data[0]?.adult_price || "",
            adminFee: basePriceData.data[0]?.adult_commission || "",
          },
          child: {
            basePrice: basePriceData.data[0]?.child_price || "",
            adminFee: basePriceData.data[0]?.child_commission || "",
          },
          infant: {
            basePrice: basePriceData.data[0]?.infant_price || "",
            adminFee: normalizeOptionalAdminFee(
              basePriceData.data[0]?.infant_commission
            ),
          },
        },
      }));
    }
  }, [basePriceData, expandedId]);

  // baseprice populating for all accordion items
  useEffect(() => {
    if (allBasePricesData && pricingData) {
      const newBasePriceByAccordion = {};
      const newIsBasePriceSettedByAccordion = {};

      pricingData.forEach((item) => {
        if (dirtyBasePriceCategoriesRef.current.has(item.id)) return;

        const basePriceInfo = allBasePricesData[item.id];

        if (basePriceInfo) {
          const hasBasePrice =
            basePriceInfo.adult_price ||
            basePriceInfo.child_price ||
            basePriceInfo.infant_price;

          newIsBasePriceSettedByAccordion[item.id] = hasBasePrice;

          newBasePriceByAccordion[item.id] = {
            id: basePriceInfo.id || "",
            adult: {
              basePrice: basePriceInfo.adult_price || "",
              adminFee: basePriceInfo.adult_commission || "",
            },
            child: {
              basePrice: basePriceInfo.child_price || "",
              adminFee: basePriceInfo.child_commission || "",
            },
            infant: {
              basePrice: basePriceInfo.infant_price || "",
              adminFee: normalizeOptionalAdminFee(basePriceInfo.infant_commission),
            },
          };
        }
      });

      setIsBasePriceSettedByAccordion((prev) => ({
        ...prev,
        ...newIsBasePriceSettedByAccordion,
      }));

      setBasePriceByAccordion((prev) => ({
        ...prev,
        ...newBasePriceByAccordion,
      }));
    }
  }, [allBasePricesData, pricingData]);

  // Process all rates response to calculate starting prices for all accordion items
  useEffect(() => {
    if (allRatesResponse && pricingData) {
      const newStartingPrices = {};

      pricingData.forEach((item) => {
        const itemRatesData = allRatesResponse[item.id] || [];

        // Process rates data similar to how it's done in the expanded accordion effect
        const processedRates = itemRatesData
          .filter((rate) => rate.package_stay_category_id === item.id)
          .map((rate) => {
            const rates = () => {
              const rateType = rate.rate_type;
              let ratTypeName = "";
              if (rateType === "normal") {
                ratTypeName = "normal_rate";
              } else if (rateType === "seasonal") {
                ratTypeName = "seasonal_rate";
              }
              return rate[ratTypeName] || {};
            };

            return {
              rateType: rate.rate_type,
              startDate: rate.start_date,
              endDate: rate.end_date,
              rates: {
                adult: {
                  basePrice: rates().adult_price || 0,
                  adminFee: rates().adult_commission || 0,
                },
                child: {
                  basePrice: rates().child_price || 0,
                  adminFee: rates().child_commission || 0,
                },
                infant: {
                  basePrice: rates().infant_price || 0,
                  adminFee: rates().infant_commission || 0,
                },
              },
              accordionId: item.id,
              applicableDays: rate?.applicable_days || null,
            };
          });

        let startingPrice = 0;

        if (processedRates.length > 0) {
          // If rates exist, find the minimum adult base price
          const minRatePrice = processedRates.reduce((min, rate) => {
            const basePrice = Number(rate.rates.adult.basePrice) || 0;
            return Math.min(min, basePrice);
          }, Infinity);

          startingPrice = minRatePrice === Infinity ? 0 : minRatePrice;
        } else {
          // If no rates exist, use base price as fallback
          const basePrice =
            basePriceByAccordion[item.id]?.adult?.basePrice || 0;
          startingPrice = Number(basePrice);
        }

        newStartingPrices[item.id] = startingPrice;
      });

      setStartingAtBasePriceByAccordion(newStartingPrices);
    }
  }, [allRatesResponse, pricingData, basePriceByAccordion]);

  // get pricing data mutation
  useEffect(() => {
    if (getPricingDataResponse) {
      // mix both data pricing and stop sales data

      const rateData = getPricingDataResponse.data.filter(
        (rate) => rate.package_stay_category_id === expandedId
      );

      console.log(listPackagePriceStopSalesData);

      // const stopSalesData = listPackagePriceStopSalesData?.data.filter(
      //   (rate) => rate.package_stay_category_id === expandedId
      // );

      const newRates = rateData.map((rate) => {
        const rates = () => {
          const rateType = rate.rate_type;
          let ratTypeName = "";
          if (rateType === "normal") {
            ratTypeName = "normal_rate";
          } else if (rateType === "seasonal") {
            ratTypeName = "seasonal_rate";
          }
          return rate[ratTypeName] || {};
        };

        return {
          id: rate.id,
          rateType: rate.rate_type,
          startDate: rate.start_date,
          endDate: rate.end_date,
          rates: {
            adult: {
              basePrice: rates().adult_price || 0,
              adminFee: rates().adult_commission || 0,
            },
            child: {
              basePrice: rates().child_price || 0,
              adminFee: rates().child_commission || 0,
            },
            infant: {
              basePrice: rates().infant_price || 0,
              adminFee: rates().infant_commission || 0,
            },
          },
          accordionId: expandedId,
          applicableDays: rate?.applicable_days || null,
        };
      });

      setSavedRatesAsPerAccordion((prevRates) => {
        const otherRates = prevRates.filter(
          (rate) => rate.accordionId !== expandedId
        );
        return [...otherRates, ...newRates];
      });
    }
  }, [getPricingDataResponse, expandedId]);

  // this is for the rate popupup popluation if similar rate is already added
  useEffect(() => {
    const rateData = recentlyAddedRates?.data;
    if (rateData && Object.keys(rateData).length) {
      setRateValues({
        adult: {
          basePrice: parseFloat(rateData.adult_price) || "",
          adminFee: parseFloat(rateData.adult_commission) || "",
        },
        child: {
          basePrice: parseFloat(rateData.child_price) || "",
          adminFee: parseFloat(rateData.child_commission) || "",
        },
        // For infant, show empty if 0 or null/undefined
        infant: {
          basePrice:
            rateData.infant_price && parseFloat(rateData.infant_price) !== 0
              ? parseFloat(rateData.infant_price)
              : "",
          adminFee:
            rateData.infant_commission &&
            parseFloat(rateData.infant_commission) !== 0
              ? parseFloat(rateData.infant_commission)
              : "",
        },
      });
    }
  }, [recentlyAddedRates]);

  const addRatesMutation = useMutation({
    mutationFn: addRates,
    onSuccess: () => {
      queryClient.invalidateQueries([
        `getExistingPricingData-${expandedId}-${packageid}`,
      ]);
      queryClient.invalidateQueries([
        `getAllRatesForStartingPrice-${packageid}`,
      ]);
      setShowToaster({
        msg: "Rates added successfully",
        type: "success",
      });
    },
    onError: (error) => {
      console.error("Error adding rates:", error);
      setShowToaster({
        msg:
          error?.response?.data?.message ||
          "Error adding rates. Please try again.",
        type: "error",
      });
    },
  });

  // Add stop sales mutation
  const addStopSalesMutation = useMutation({
    mutationFn: addPackagePriceStopSales,
    onSuccess: () => {
      queryClient.invalidateQueries([
        `listPackagePriceStopSales-${packageid}-${expandedId}`,
      ]);
      setShowToaster({
        msg: "Stop sales added successfully",
        type: "success",
      });
    },
    onError: (error) => {
      console.error("Error adding stop sales:", error);
      setShowToaster({
        msg:
          error?.response?.data?.message ||
          "Error adding stop sales. Please try again.",
        type: "error",
      });
    },
  });

  // Delete rate mutation
  const deleteRateMutation = useMutation({
    mutationFn: deleteRate,
    onSuccess: (_, rateId) => {
      // Remove the rate from local state
      setSavedRatesAsPerAccordion((prevRates) =>
        prevRates.filter((rate) => rate.id !== rateId)
      );
      queryClient.invalidateQueries([
        `getExistingPricingData-${expandedId}-${packageid}`,
      ]);
      queryClient.invalidateQueries([
        `getAllRatesForStartingPrice-${packageid}`,
      ]);
      setShowToaster({
        msg: "Rate deleted successfully",
        type: "success",
      });
    },
    onError: (error) => {
      console.error("Error deleting rate:", error);
      setShowToaster({
        msg:
          error?.response?.data?.message ||
          "Error deleting rate. Please try again.",
        type: "error",
      });
    },
  });

  // Update rate mutation
  const updateRateMutation = useMutation({
    mutationFn: ({ rateId, data }) => updateRate(rateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries([
        `getExistingPricingData-${expandedId}-${packageid}`,
      ]);
      queryClient.invalidateQueries([
        `getAllRatesForStartingPrice-${packageid}`,
      ]);
      setShowToaster({
        msg: "Rate updated successfully",
        type: "success",
      });
    },
    onError: (error) => {
      console.error("Error updating rate:", error);
      setShowToaster({
        msg:
          error?.response?.data?.message ||
          "Error updating rate. Please try again.",
        type: "error",
      });
    },
  });

  // Delete stop sale mutation
  const deleteStopSaleMutation = useMutation({
    mutationFn: deleteStopSale,
    onSuccess: () => {
      queryClient.invalidateQueries([
        `listPackagePriceStopSales-${packageid}-${expandedId}`,
      ]);
      queryClient.invalidateQueries([
        `getAllRatesForStartingPrice-${packageid}`,
      ]);
      setShowToaster({
        msg: "Stop sale deleted successfully",
        type: "success",
      });
    },
    onError: (error) => {
      console.error("Error deleting stop sale:", error);
      setShowToaster({
        msg: "Error deleting stop sale. Please try again.",
        type: "error",
      });
    },
  });

  // Update stop sale mutation
  // const updateStopSaleMutation = useMutation({
  //   mutationFn: ({ stopSaleId, data }) => updateStopSale(stopSaleId, data),
  //   onSuccess: () => {
  //     queryClient.invalidateQueries([`listPackagePriceStopSales-${packageid}-${expandedId}`]);
  //     setShowToaster({
  //       msg: "Stop sale updated successfully",
  //       type: "success",
  //     });
  //   },
  //   onError: (error) => {
  //     console.error("Error updating stop sale:", error);
  //     setShowToaster({
  //       msg: "Error updating stop sale. Please try again.",
  //       type: "error",
  //     });
  //   },
  // });

  const resetRateValues = () => {
    setRateValues(initialRateState);
  };

  const handleRateTypeChange = (accordionId, type) => {
    notifyUserEdit();
    setRateTypesByAccordion((prev) => ({
      ...prev,
      [accordionId]: type,
    }));
    setChoosedDatesByAccordion((prev) => ({
      ...prev,
      [accordionId]: { startDate: "", endDate: "" },
    }));
    resetRateValues();
  };

  const handleDateChange = (e) => {
    notifyUserEdit();
    const { name, value } = e.target;
    setChoosedDatesByAccordion((prev) => ({
      ...prev,
      [expandedId]: {
        ...(prev[expandedId] || { startDate: "", endDate: "" }),
        [name]: value,
      },
    }));
  };

  const handleStopSaleDateChange = (e) => {
    notifyUserEdit();
    const { name, value } = e.target;
    setStopSaleDatesByAccordion((prev) => ({
      ...prev,
      [expandedId]: {
        ...(prev[expandedId] || { startDate: "", endDate: "" }),
        [name]: value,
      },
    }));
  };

  const handleRateValueChange = (personType, field, value) => {
    notifyUserEdit();
    if (field === "adminFee") {
      const nextValue = value === "" ? "" : Number(value);
      if (nextValue !== "" && !isValidAdminCharge(nextValue)) {
        setShowToaster({
          msg: "Admin fee must be 5%, 10%, 15%, or 20%",
          type: "error",
        });
        return;
      }

      setRateValues((prev) => ({
        ...prev,
        [personType]: {
          ...prev[personType],
          [field]: nextValue,
        },
      }));
      return;
    }

    const sanitizedValue = sanitizePriceInput(value);
    if (!isValidPartialPriceInput(sanitizedValue)) return;

    if (sanitizedValue !== "" && sanitizedValue !== ".") {
      const numValue = parseFloat(sanitizedValue);
      if (!isNaN(numValue) && numValue > 1000000) {
        setShowToaster({
          msg: "Rate cannot exceed 1,000,000",
          type: "error",
        });
        return;
      }
    }

    setRateValues((prev) => ({
      ...prev,
      [personType]: {
        ...prev[personType],
        [field]: sanitizedValue,
      },
    }));
  };

  const calculateBalance = calculateNetReceive;

  const rateCounts = useMemo(() => {
    if (!pricingData) return {};
    return pricingData.reduce((acc, item) => {
      acc[item.id] = savedRatesAsPerAccordion.filter(
        (rate) => rate.accordionId === item.id
      ).length;
      return acc;
    }, {});
  }, [pricingData, savedRatesAsPerAccordion]);

  const activeAdultPrice =
    basePriceByAccordion[expandedId]?.adult?.basePrice ?? "";

  const handlePaymentModeChange = (mode) => {
    notifyUserEdit();
    setPaymentMode(mode);
    if (mode === "full") {
      setPartialAmount(100);
    } else if (partialAmount === 100 || partialAmount === "") {
      setPartialAmount("");
    }
  };

  const stabilizePricingSection = useCallback(() => {
    onPinSection?.("pricing", { durationMs: 6000, scrollIntoView: true });
  }, [onPinSection]);

  const openRatePanel = (rateType) => {
    if (!expandedId) return;
    onPinSection?.("pricing", { durationMs: 10000 });
    setRateTypesByAccordion((prev) => ({
      ...prev,
      [expandedId]: rateType,
    }));
    setChoosedDatesByAccordion((prev) => ({
      ...prev,
      [expandedId]: { startDate: "", endDate: "" },
    }));
    setIsEditing(false);
    setEditingRate(null);
    resetRateValues();
    setShowRateDialouge(true);
  };

  const handleSubmitStopSale = () => {
    const dates = stopSaleDatesByAccordion[expandedId];
    if (
      !expandedId ||
      !dates?.startDate ||
      !dates?.endDate ||
      stopSaleSelectedDays.length === 0
    ) {
      setShowToaster({
        msg: "Please select date range and at least one day",
        type: "error",
      });
      return;
    }

    const formattedDataToSend = {
      package_id: packageid,
      package_stay_category_id: expandedId,
      stay_category_id: pricingData?.find((item) => item.id === expandedId)
        ?.stay_category_id,
      start_date: dates.startDate,
      end_date: dates.endDate,
      stop_sale_applicable_days: JSON.stringify({
        sun: stopSaleSelectedDays.includes("Sun"),
        mon: stopSaleSelectedDays.includes("Mon"),
        tue: stopSaleSelectedDays.includes("Tue"),
        wed: stopSaleSelectedDays.includes("Wed"),
        thu: stopSaleSelectedDays.includes("Thu"),
        fri: stopSaleSelectedDays.includes("Fri"),
        sat: stopSaleSelectedDays.includes("Sat"),
      }),
    };

    addStopSalesMutation.mutate(formattedDataToSend, {
      onSuccess: () => {
        queryClient.invalidateQueries([
          `listPackagePriceStopSales-${packageid}-${expandedId}`,
        ]);
        queryClient.invalidateQueries([
          `getAllRatesForStartingPrice-${packageid}`,
        ]);
        setStopSaleSelectedDays([]);
        setStopSaleDatesByAccordion((prev) => ({
          ...prev,
          [expandedId]: { startDate: "", endDate: "" },
        }));
      },
    });
  };

  const handleDayToggle = (day) => {
    notifyUserEdit();
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleStopSaleDayToggle = (day) => {
    notifyUserEdit();
    setStopSaleSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleAllDaysToggle = () => {
    notifyUserEdit();
    setSelectedDays(
      selectedDays.length === WEEKDAYS.length ? [] : WEEKDAYS.map((d) => d.day)
    );
  };

  const handleStopSaleAllDaysToggle = () => {
    notifyUserEdit();
    setStopSaleSelectedDays(
      stopSaleSelectedDays.length === WEEKDAYS.length
        ? []
        : WEEKDAYS.map((d) => d.day)
    );
  };

  const validateDates = (startDate, endDate, isEditing = false) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only check for past dates when adding new rates, not when editing existing ones
    if (!isEditing && start < today) {
      setShowToaster({
        msg: "Start date cannot be in the past",
        type: "error",
      });
      return false;
    }

    if (end < start) {
      setShowToaster({
        msg: "End date must be after start date",
        type: "error",
      });
      return false;
    }

    const oneYear = 365 * 24 * 60 * 60 * 1000;
    if (end - start > oneYear) {
      setShowToaster({
        msg: "Date range cannot exceed 1 year",
        type: "error",
      });
      return false;
    }

    return true;
  };

  const validateRates = (rates) => {
    if (!rates.adult.basePrice || !rates.child.basePrice) {
      setShowToaster({
        msg: "Adult and Child base prices are mandatory",
        type: "error",
      });
      return false;
    }

    if (!rates.adult.adminFee || !rates.child.adminFee) {
      setShowToaster({
        msg: "Adult and Child admin fees are mandatory",
        type: "error",
      });
      return false;
    }

    for (const [personType, values] of Object.entries(rates)) {
      // Allow empty infant fields (will be treated as 0)
      if (personType === "infant") {
        const basePrice =
          values.basePrice === "" ||
          values.basePrice === null ||
          values.basePrice === undefined
            ? 0
            : parseFloat(values.basePrice) || 0;
        const adminFee =
          values.adminFee === "" ||
          values.adminFee === null ||
          values.adminFee === undefined
            ? 0
            : parseFloat(values.adminFee) || 0;

        // Only validate if values are provided (not empty)
        if (
          values.basePrice !== "" &&
          values.basePrice !== null &&
          values.basePrice !== undefined
        ) {
          if (isNaN(basePrice) || basePrice < 0) {
            setShowToaster({
              msg: `Invalid infant base price. Please enter a valid number.`,
              type: "error",
            });
            return false;
          }
        }

        if (
          values.adminFee !== "" &&
          values.adminFee !== null &&
          values.adminFee !== undefined
        ) {
          if (!isValidAdminCharge(adminFee)) {
            setShowToaster({
              msg: `Infant admin fee must be 5%, 10%, 15%, or 20%`,
              type: "error",
            });
            return false;
          }
        }
        continue; // Skip further validation for infant if empty
      }

      const basePrice = parseFloat(values.basePrice);
      const adminFee = parseFloat(values.adminFee);

      if (isNaN(basePrice) || isNaN(adminFee)) {
        setShowToaster({
          msg: `Invalid ${personType} rates. Please enter valid numbers.`,
          type: "error",
        });
        return false;
      }

      if (basePrice <= 0) {
        setShowToaster({
          msg: `${personType} base price must be greater than 0`,
          type: "error",
        });
        return false;
      }

      if (!isValidAdminCharge(adminFee)) {
        setShowToaster({
          msg: `${personType} admin fee must be 5%, 10%, 15%, or 20%`,
          type: "error",
        });
        return false;
      }
    }

    return true;
  };

  const handleApplyRate = async () => {
    const currentRateType = rateTypesByAccordion[expandedId];
    const currentDates = choosedDatesByAccordion[expandedId] || {};

    if (!currentRateType || !currentDates.startDate || !currentDates.endDate) {
      setShowToaster({
        msg: "Please select rate type and date range",
        type: "error",
      });
      return;
    }

    if (
      !validateDates(currentDates.startDate, currentDates.endDate, isEditing)
    ) {
      return;
    }

    if (currentRateType === "stop" && selectedDays.length === 0) {
      setShowToaster({
        msg: "Please select at least one day for stop sale",
        type: "error",
      });
      return;
    }

    if (currentRateType !== "stop" && !validateRates(rateValues)) {
      return;
    }

    const newStartDate = new Date(currentDates.startDate).getTime();
    const newEndDate = new Date(currentDates.endDate).getTime();

    // Check for overlapping dates, but exclude the rate being edited
    const isDateOverlapping = savedRatesAsPerAccordion.some((rate) => {
      // Skip the rate being edited
      if (isEditing && editingRate && rate.id === editingRate.id) {
        return false;
      }

      if (
        rate.accordionId !== expandedId ||
        rate.rateType !== currentRateType
      ) {
        return false;
      }

      const existingStartDate = new Date(rate.startDate).getTime();
      const existingEndDate = new Date(rate.endDate).getTime();

      const overlap =
        (newStartDate >= existingStartDate &&
          newStartDate <= existingEndDate) ||
        (newEndDate >= existingStartDate && newEndDate <= existingEndDate) ||
        (newStartDate <= existingStartDate && newEndDate >= existingEndDate);

      if (currentRateType === "stop" && overlap) {
        const hasCommonDays = selectedDays.some((day) =>
          rate.applicableDays?.includes(day)
        );
        return hasCommonDays;
      }

      return overlap;
    });

    if (isDateOverlapping) {
      setShowToaster({
        msg: `A ${currentRateType} rate already exists for these dates${
          currentRateType === "stop" ? " and selected days" : ""
        }`,
        type: "error",
      });
      return;
    }

    const rateData = {
      id: isEditing && editingRate ? editingRate.id : undefined, // Preserve ID when editing
      rateType: currentRateType,
      startDate: currentDates.startDate,
      endDate: currentDates.endDate,
      rates: rateValues,
      accordionId: expandedId,
      applicableDays: currentRateType === "stop" ? selectedDays : null,
    };

    const newRateData = {
      start_date: rateData.startDate,
      end_date: rateData.endDate,
      package_id: packageid,
      package_stay_category_id: expandedId,
      stay_category_id: pricingData?.find((item) => item.id === expandedId)
        ?.stay_category_id,
      rate_type: rateData.rateType,
      adult_price: rateData.rates.adult.basePrice,
      child_price: rateData.rates.child.basePrice,
      // Treat empty infant fields as 0
      infant_price:
        rateData.rates.infant.basePrice === "" ||
        rateData.rates.infant.basePrice === null ||
        rateData.rates.infant.basePrice === undefined
          ? 0
          : rateData.rates.infant.basePrice,
      adult_commission: rateData.rates.adult.adminFee,
      child_commission: rateData.rates.child.adminFee,
      // Treat empty infant admin fee as 0
      infant_commission:
        rateData.rates.infant.adminFee === "" ||
        rateData.rates.infant.adminFee === null ||
        rateData.rates.infant.adminFee === undefined
          ? 0
          : rateData.rates.infant.adminFee,
    };

    try {
      if (isEditing && editingRate) {
        // Update existing rate
        if (!editingRate.id) {
          setShowToaster({
            msg: "Rate ID is missing. Cannot update rate.",
            type: "error",
          });
          return;
        }
        await updateRateMutation.mutateAsync({
          rateId: editingRate.id,
          data: newRateData,
        });

        // Update the rate in the local state by matching ID
        const updatedRates = savedRatesAsPerAccordion.map((rate) =>
          rate.id === editingRate.id
            ? { ...rate, ...rateData, id: editingRate.id } // Preserve the ID
            : rate
        );
        setSavedRatesAsPerAccordion(updatedRates);

        // Don't manually call setPricingData here - let the useEffect handle it
        // This ensures all pricing data (payment_terms, etc.) is preserved
      } else {
        // Add new rate
        await addRatesMutation.mutateAsync(newRateData);

        const newRates = [...savedRatesAsPerAccordion, rateData];
        setSavedRatesAsPerAccordion(newRates);

        // Don't manually call setPricingData here - let the useEffect handle it
        // This ensures all pricing data (payment_terms, etc.) is preserved
      }

      setShowRateDialouge(false);
      setSelectedDays([]);
      setIsEditing(false);
      setEditingRate(null);
      queryClient.removeQueries([
        `recentlyAddedRates-${rateTypesByAccordion[expandedId]}-${expandedId}`,
      ]);
      resetRateValues();
      setChoosedDatesByAccordion((prev) => ({
        ...prev,
        [expandedId]: { startDate: "", endDate: "" },
      }));
      requestAnimationFrame(() => {
        requestAnimationFrame(stabilizePricingSection);
      });
    } catch (error) {
      console.error("Error processing rate:", error);
      setShowToaster({
        msg: `Error ${
          isEditing ? "updating" : "adding"
        } rate. Please try again.`,
        type: "error",
      });
    }
  };

  const closeHandler = () => {
    setShowRateDialouge(false);
    setIsEditing(false);
    setEditingRate(null);
    // setEditingStopSale(null);
  };

  // Handle edit rate
  const handleEditRate = (rate) => {
    setIsEditing(true);
    setEditingRate(rate);

    // Set the rate type for the accordion
    setRateTypesByAccordion((prev) => ({
      ...prev,
      [rate.accordionId]: rate.rateType,
    }));

    // Set the dates
    setChoosedDatesByAccordion((prev) => ({
      ...prev,
      [rate.accordionId]: {
        startDate: rate.startDate,
        endDate: rate.endDate,
      },
    }));

    // Set the rate values, converting 0 infant values to empty string for display
    setRateValues({
      adult: rate.rates.adult || { basePrice: "", adminFee: "" },
      child: rate.rates.child || { basePrice: "", adminFee: "" },
      infant: {
        basePrice:
          rate.rates.infant?.basePrice && rate.rates.infant.basePrice !== 0
            ? rate.rates.infant.basePrice
            : "",
        adminFee:
          rate.rates.infant?.adminFee && rate.rates.infant.adminFee !== 0
            ? rate.rates.infant.adminFee
            : "",
      },
    });

    // Set selected days for stop sale
    if (rate.rateType === "stop" && rate.applicableDays) {
      setSelectedDays(rate.applicableDays);
    }

    setShowRateDialouge(true);
  };

  // Handle edit stop sale
  const handleEditStopSale = (stopSale) => {
    setIsEditing(true);
    // Store stop sale for editing (using editingRate state for consistency)
    setEditingRate({
      id: stopSale.id,
      accordionId: stopSale.package_stay_category_id,
      rateType: "stop",
      startDate: stopSale.start_date,
      endDate: stopSale.end_date,
      applicableDays: (() => {
        const days = stopSale.stop_sale_applicable_days;
        const activeDays = [];
        if (days.sun) activeDays.push("Sun");
        if (days.mon) activeDays.push("Mon");
        if (days.tue) activeDays.push("Tue");
        if (days.wed) activeDays.push("Wed");
        if (days.thu) activeDays.push("Thu");
        if (days.fri) activeDays.push("Fri");
        if (days.sat) activeDays.push("Sat");
        return activeDays;
      })(),
    });

    // Set the rate type for the accordion
    setRateTypesByAccordion((prev) => ({
      ...prev,
      [stopSale.package_stay_category_id]: "stop",
    }));

    // Set the dates
    setChoosedDatesByAccordion((prev) => ({
      ...prev,
      [stopSale.package_stay_category_id]: {
        startDate: stopSale.start_date,
        endDate: stopSale.end_date,
      },
    }));

    // Set selected days from stop sale data
    const days = stopSale.stop_sale_applicable_days;
    const activeDays = [];
    if (days.sun) activeDays.push("Sun");
    if (days.mon) activeDays.push("Mon");
    if (days.tue) activeDays.push("Tue");
    if (days.wed) activeDays.push("Wed");
    if (days.thu) activeDays.push("Thu");
    if (days.fri) activeDays.push("Fri");
    if (days.sat) activeDays.push("Sat");

    setSelectedDays(activeDays);
    setShowRateDialouge(true);
  };

  // Handle delete rate
  const handleDeleteRate = (rateId) => {
    if (!rateId) {
      setShowToaster({
        msg: "Rate ID is missing. Cannot delete rate.",
        type: "error",
      });
      return;
    }
    if (window.confirm("Are you sure you want to delete this rate?")) {
      deleteRateMutation.mutate(rateId);
    }
  };

  // Handle delete stop sale
  const handleDeleteStopSale = (stopSaleId) => {
    if (window.confirm("Are you sure you want to delete this stop sale?")) {
      deleteStopSaleMutation.mutate(stopSaleId);
    }
  };

  const handleBasePriceChange = (type, field, value) => {
    notifyUserEdit();
    if (expandedId) {
      dirtyBasePriceCategoriesRef.current.add(expandedId);
    }

    if (field === "adminFee") {
      const nextValue = value === "" ? "" : Number(value);
      if (nextValue !== "" && !isValidAdminCharge(nextValue)) {
        setShowToaster({
          msg: "Admin fee must be 5%, 10%, 15%, or 20%",
          type: "error",
        });
        return;
      }

      if (type === "adult" && nextValue !== "") {
        setGlobalAdminFee(nextValue);
      }

      setBasePriceByAccordion((prev) => ({
        ...prev,
        [expandedId]: {
          id: basePriceByAccordion[expandedId]?.id || "",
          ...(prev[expandedId] || {}),
          [type]: {
            ...(prev[expandedId]?.[type] || {}),
            [field]: nextValue,
          },
        },
      }));
      return;
    }

    const sanitizedValue = sanitizePriceInput(value);
    if (!isValidPartialPriceInput(sanitizedValue)) return;

    if (sanitizedValue !== "" && sanitizedValue !== ".") {
      const numValue = parseFloat(sanitizedValue);
      if (!isNaN(numValue) && numValue > 1000000) {
        setShowToaster({
          msg: "Price cannot exceed 1,000,000",
          type: "error",
        });
        return;
      }
    }

    setBasePriceByAccordion((prev) => ({
      ...prev,
      [expandedId]: {
        id: basePriceByAccordion[expandedId]?.id || "",
        ...(prev[expandedId] || {}),
        [type]: {
          ...(prev[expandedId]?.[type] || {}),
          [field]: sanitizedValue,
        },
      },
    }));
  };

  const resolveBasePriceForCategory = useCallback(
    (categoryId) => {
      const raw = basePriceByAccordion[categoryId] || {};

      return {
        id: raw.id || allBasePricesData?.[categoryId]?.id || "",
        adult: {
          basePrice: raw.adult?.basePrice ?? "",
          adminFee: raw.adult?.adminFee ?? "",
        },
        child: {
          basePrice: raw.child?.basePrice ?? "",
          adminFee: raw.child?.adminFee ?? "",
        },
        infant: {
          basePrice: raw.infant?.basePrice ?? "",
          adminFee: raw.infant?.adminFee ?? "",
        },
      };
    },
    [basePriceByAccordion, allBasePricesData]
  );

  const toCommissionPayload = (adminFee) => {
    if (adminFee === "" || adminFee === null || adminFee === undefined) {
      return 0;
    }
    return adminFee;
  };

  const isBasePriceFormComplete = useCallback(
    (categoryId) =>
      Object.keys(validateBasePriceFields(resolveBasePriceForCategory(categoryId)))
        .length === 0,
    [resolveBasePriceForCategory]
  );

  const canManageRates = useCallback(
    (categoryId) =>
      Boolean(isBasePriceSettedByAccordion[categoryId]) ||
      isBasePriceFormComplete(categoryId),
    [isBasePriceSettedByAccordion, isBasePriceFormComplete]
  );

  const saveAllBasePrices = useCallback(async () => {
    if (!pricingData?.length) {
      throw new Error("No stay categories configured");
    }

    let firstErrorCategory = null;
    let firstErrors = {};

    for (const category of pricingData) {
      const errors = validateBasePriceFields(
        resolveBasePriceForCategory(category.id)
      );
      if (Object.keys(errors).length > 0 && !firstErrorCategory) {
        firstErrorCategory = category.id;
        firstErrors = errors;
      }
    }

    if (firstErrorCategory) {
      setExpandedId(firstErrorCategory);
      setBasePriceErrors(firstErrors);
      throw new Error(
        "Please fill in all required base price fields for every stay category"
      );
    }

    setBasePriceErrors({});
    const updatedBasePrices = { ...basePriceByAccordion };

    const saveResults = await Promise.all(
      pricingData.map(async (category) => {
        const resolved = resolveBasePriceForCategory(category.id);
        updatedBasePrices[category.id] = resolved;

        const payload = {
          package_id: packageid,
          package_stay_category_id: category.id,
          stay_category_id: category.stay_category_id,
          adult_price: parseAmount(resolved.adult.basePrice),
          child_price: parseAmount(resolved.child.basePrice),
          infant_price: parseAmount(resolved.infant.basePrice),
          adult_commission: toCommissionPayload(resolved.adult.adminFee),
          child_commission: toCommissionPayload(resolved.child.adminFee),
          infant_commission: toCommissionPayload(resolved.infant.adminFee),
        };

        const existingId =
          basePriceByAccordion[category.id]?.id ||
          allBasePricesData?.[category.id]?.id;

        if (existingId) {
          await updateBasePrice(payload, existingId);
          return { categoryId: category.id, resolved, id: existingId };
        }

        const response = await insertBasePrice(payload);
        const createdId =
          response?.data?.id ||
          response?.data?.[0]?.id ||
          response?.id ||
          "";
        return { categoryId: category.id, resolved, id: createdId };
      })
    );

    saveResults.forEach(({ categoryId, resolved, id }) => {
      updatedBasePrices[categoryId] = id ? { ...resolved, id } : resolved;
    });

    setBasePriceByAccordion(updatedBasePrices);
    setIsBasePriceSettedByAccordion((prev) => {
      const next = { ...prev };
      pricingData.forEach((cat) => {
        next[cat.id] = true;
        dirtyBasePriceCategoriesRef.current.delete(cat.id);
      });
      return next;
    });
  }, [
    pricingData,
    resolveBasePriceForCategory,
    basePriceByAccordion,
    allBasePricesData,
    packageid,
  ]);

  const validateForSave = useCallback(() => {
    if (!pricingData?.length) {
      return { valid: false, message: "No stay categories configured" };
    }

    for (const category of pricingData) {
      const resolved = resolveBasePriceForCategory(category.id);
      const errors = validateBasePriceFields(resolved);
      if (Object.keys(errors).length > 0) {
        setExpandedId(category.id);
        setBasePriceErrors(errors);
        const categoryName =
          category.stay_category?.title || "this stay category";
        return {
          valid: false,
          message: `Please complete base price for ${categoryName} (adult & child price and admin fee required)`,
          categoryId: category.id,
        };
      }
    }

    if (paymentMode === "partial") {
      const deposit = Number(partialAmount);
      if (!partialAmount || deposit <= 0 || deposit >= 100) {
        return {
          valid: false,
          message: "Please enter a valid deposit percentage (1–99%)",
        };
      }
      if (paymentDays === "" || paymentDays === null || paymentDays === undefined) {
        return {
          valid: false,
          message: "Please enter balance due days before travel",
        };
      }
    }

    setBasePriceErrors({});
    return { valid: true };
  }, [
    pricingData,
    resolveBasePriceForCategory,
    paymentMode,
    partialAmount,
    paymentDays,
  ]);

  const getSaveAndContinuePayload = useCallback(() => {
    if (!pricingData?.length) return null;

    const categoriesWithPrices = pricingData.map((category) => {
      const resolved = resolveBasePriceForCategory(category.id);
      const adultBasePrice = Number(resolved.adult.basePrice) || 0;
      const categoryRates = savedRatesAsPerAccordion.filter(
        (rate) => rate.accordionId === category.id
      );

      let startingPrice = adultBasePrice;
      if (categoryRates.length > 0) {
        const minRatePrice = categoryRates.reduce((min, rate) => {
          const basePrice = Number(rate.rates?.adult?.basePrice) || 0;
          return basePrice > 0 ? Math.min(min, basePrice) : min;
        }, Infinity);
        if (minRatePrice !== Infinity) {
          startingPrice = minRatePrice;
        }
      }

      return {
        stay_category_id: category.stay_category_id,
        starting_price: startingPrice,
      };
    });

    const lowestBasePrice = categoriesWithPrices.reduce(
      (min, category) => Math.min(min, category.starting_price ?? Infinity),
      Infinity
    );
    const defaultCategory =
      categoriesWithPrices.find(
        (category) => category.starting_price === lowestBasePrice
      ) ?? categoriesWithPrices[0];

    const isPartialPayment = paymentMode === "partial";

    return {
      default_stay_category_id: defaultCategory?.stay_category_id,
      discount,
      payment_type: isPartialPayment ? "partial" : "full",
      advance_percentage: isPartialPayment ? Number(partialAmount) : 100,
      pay_before: paymentDays ? Number(paymentDays) : null,
    };
  }, [
    pricingData,
    resolveBasePriceForCategory,
    savedRatesAsPerAccordion,
    paymentMode,
    partialAmount,
    paymentDays,
    discount,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      saveAllBasePrices,
      validateForSave,
      getSaveAndContinuePayload,
    }),
    [saveAllBasePrices, validateForSave, getSaveAndContinuePayload]
  );

  useEffect(() => {
    if (!pricingData?.length) return;

    if (!expandedId || !pricingData.some((item) => item.id === expandedId)) {
      setExpandedId(pricingData[0].id);
    }
  }, [pricingData, expandedId]);

  useEffect(() => {
    if (basePriceData?.data?.[0]?.adult_commission && expandedId) {
      setGlobalAdminFee(basePriceData.data[0].adult_commission);
    }
  }, [basePriceData, expandedId]);

  useEffect(() => {
    resetRateValues();
  }, [expandedId]);

  // Clear editing state when dialog closes
  useEffect(() => {
    if (!showRateDialouge) {
      setIsEditing(false);
      setEditingRate(null);
      // setEditingStopSale(null);
      setSelectedDays([]);
    }
  }, [showRateDialouge]);

  useEffect(() => {
    if (listPackagePriceStopSalesData?.data) {
      setStopSalesData(listPackagePriceStopSalesData.data);
    }
  }, [listPackagePriceStopSalesData]);

  // Update the effect that handles setPricingData
  useEffect(() => {
    if (!pricingData) return;

    const formattedData = pricingData.map((category) => ({
      ...category,
      rates: savedRatesAsPerAccordion.filter(
        (rate) => rate.accordionId === category.id
      ),
      base_price: (() => {
        const raw = basePriceByAccordion[category.id];
        if (!raw) return null;
        return resolveBasePriceForCategory(category.id);
      })(),
      stop_sales: stopSalesData.filter(
        (sale) => sale.package_stay_category_id === category.id
      ),
      starting_price: startingAtBasePriceByAccordion[category.id] || 0,
      is_base_price_set: isBasePriceSettedByAccordion[category.id] || false,
    }));

    const newPricingData = {
      package_id: packageid,
      categories: formattedData,
      payment_terms: {
        discount_percentage: discount,
        partial_amount_percentage: partialAmount ? Number(partialAmount) : null,
        payment_days: paymentDays ? Number(paymentDays) : null,
      },
      rate_types: rateTypesByAccordion,
      dates_by_accordion: choosedDatesByAccordion,
      data: savedRatesAsPerAccordion,
      stop_sales: stopSalesData,
    };

    // Prevent infinite loop by checking if data has actually changed
    // Include rate IDs to detect when rates are edited (not just count)
    const dataKey = JSON.stringify({
      categories: formattedData.map((c) => ({
        id: c.id,
        ratesCount: c.rates?.length || 0,
        rateIds:
          c.rates
            ?.map(
              (r) => r.id || `${r.accordionId}-${r.startDate}-${r.rateType}`
            )
            .sort() || [],
        basePrice: c.base_price?.id || null,
        stopSalesCount: c.stop_sales?.length || 0,
        stopSaleIds: c.stop_sales?.map((s) => s.id).sort() || [],
      })),
      discount,
      partialAmount,
      paymentDays,
      ratesCount: savedRatesAsPerAccordion.length,
      rateIds: savedRatesAsPerAccordion
        .map((r) => r.id || `${r.accordionId}-${r.startDate}-${r.rateType}`)
        .sort(),
      stopSalesCount: stopSalesData.length,
      stopSaleIds: stopSalesData.map((s) => s.id).sort(),
    });

    if (pricingDataUpdateRef.current !== dataKey) {
      pricingDataUpdateRef.current = dataKey;
      setPricingData(newPricingData);
    }
  }, [
    pricingData,
    savedRatesAsPerAccordion,
    basePriceByAccordion,
    stopSalesData,
    startingAtBasePriceByAccordion,
    isBasePriceSettedByAccordion,
    discount,
    partialAmount,
    paymentDays,
    rateTypesByAccordion,
    choosedDatesByAccordion,
    packageid,
    setPricingData,
    resolveBasePriceForCategory,
  ]);

  // Validate payment terms inputs
  const handlePartialAmountChange = (e) => {
    notifyUserEdit();
    const value = e.target.value;
    if (value === "" || (Number(value) >= 0 && Number(value) <= 100)) {
      setPartialAmount(value);
      if (value && Number(value) < 100) {
        setPaymentMode("partial");
      }
    }
  };

  const handlePaymentDaysChange = (e) => {
    notifyUserEdit();
    const value = e.target.value;
    if (value === "" || Number(value) >= 0) {
      setPaymentDays(value);
    }
  };

  const colors = {
    normal: "bg-green-100",
    seasonal: "bg-blue-100",
    stop: "bg-red-100",
  };

  const rateTypes = [
    {
      id: 1,
      title: "Normal",
      type: "normal",
      color: "bg-green-100",
      description: "Normal season is where the price is the same for all days",
    },
    {
      id: 2,
      title: "Seasonal",
      type: "seasonal",
      color: "bg-blue-100",
      description:
        "Seasonal period is where the price is different for different days",
    },
    {
      id: 5,
      title: "Stop Sale",
      type: "stop",
      color: "bg-red-100",
      description:
        "Stop sale is where the price is not available for certain days",
    },
  ];

  const formatDate = formatPricingDate;

  // Effect to populate discount and payment terms from API response
  useEffect(() => {
    if (discountAndPaymentData?.data) {
      setDiscount(discountAndPaymentData.data.discount || 0);
      const advance = discountAndPaymentData.data.advance_percentage;
      setPartialAmount(advance || "");
      setPaymentDays(discountAndPaymentData.data.pay_before || "");
      if (
        advance === 100 ||
        advance === "100" ||
        advance === null ||
        advance === "" ||
        advance === undefined
      ) {
        setPaymentMode("full");
        if (!advance) setPartialAmount(100);
      } else {
        setPaymentMode("partial");
      }
    }
  }, [discountAndPaymentData]);

  return (
    <>
      <p className="text-sm text-gray-500 mb-4">
        Set your base price, add seasonal rates, and configure payment rules.
      </p>

      <StayCategoryTabs
        categories={pricingData}
        activeId={expandedId}
        onSelect={handleCategorySelect}
        startingPrices={startingAtBasePriceByAccordion}
        rateCounts={rateCounts}
      />

      <div className="space-y-5 min-w-0">
          {expandedId && (
            <>
              <BasePriceSection
                basePrice={basePriceByAccordion[expandedId]}
                basePriceErrors={basePriceErrors}
                onPaxChange={handleBasePriceChange}
                isBasePriceSaved={Boolean(
                  isBasePriceSettedByAccordion[expandedId]
                )}
                isFormComplete={isBasePriceFormComplete(expandedId)}
              />

              <SeasonalPricingSection
                categoryId={expandedId}
                isEnabled={canManageRates(expandedId)}
                savedRates={savedRatesAsPerAccordion}
                colors={colors}
                calculateBalance={calculateBalance}
                formatDate={formatDate}
                weekdays={WEEKDAYS}
                onAddSeasonal={() => openRatePanel("seasonal")}
                onEditRate={handleEditRate}
                onDeleteRate={handleDeleteRate}
              />

              <StopSaleSection
                categoryId={expandedId}
                isEnabled={canManageRates(expandedId)}
                stopSales={stopSalesData}
                stopSaleDates={stopSaleDatesByAccordion[expandedId]}
                selectedDays={stopSaleSelectedDays}
                colors={colors}
                calculateBalance={calculateBalance}
                formatDate={formatDate}
                weekdays={WEEKDAYS}
                onStopSaleDateChange={handleStopSaleDateChange}
                onDayToggle={handleStopSaleDayToggle}
                onAllDaysToggle={handleStopSaleAllDaysToggle}
                onSubmitStopSale={handleSubmitStopSale}
                onEditStopSale={handleEditStopSale}
                onDeleteStopSale={handleDeleteStopSale}
                isSubmittingStopSale={addStopSalesMutation.isPending}
              />
            </>
          )}

          <PaymentRulesSection
            paymentMode={paymentMode}
            onPaymentModeChange={handlePaymentModeChange}
            partialAmount={partialAmount}
            onPartialAmountChange={handlePartialAmountChange}
            paymentDays={paymentDays}
            onPaymentDaysChange={handlePaymentDaysChange}
            previewPrice={Number(activeAdultPrice) || 0}
          />

          <AdvancedPricingAccordion
            discount={discount}
            onDiscountChange={(value) => {
              notifyUserEdit();
              setDiscount(value);
            }}
          />
      </div>

      <SeasonalRatePanel
        open={showRateDialouge}
        isEditing={isEditing}
        rateTypeTitle={
          rateTypes.find(
            (r) => r.type === rateTypesByAccordion[expandedId]
          )?.title || "Rate"
        }
        startDate={
          (choosedDatesByAccordion[expandedId] || {}).startDate || ""
        }
        endDate={(choosedDatesByAccordion[expandedId] || {}).endDate || ""}
        rateValues={rateValues}
        selectedDays={selectedDays}
        isStopSale={rateTypesByAccordion[expandedId] === "stop"}
        onClose={closeHandler}
        onApply={
          rateTypesByAccordion[expandedId] === "stop"
            ? handleSubmitStopSale
            : handleApplyRate
        }
        onDateChange={handleDateChange}
        onRateValueChange={handleRateValueChange}
        onDayToggle={handleDayToggle}
        onAllDaysToggle={handleAllDaysToggle}
        isSubmitting={
          addRatesMutation.isPending ||
          updateRateMutation.isPending ||
          addStopSalesMutation.isPending
        }
      />

      {showToaster && (
        <Toaster
          message={showToaster.msg}
          type={showToaster.type}
          duration={3000}
          onClose={() => setShowToaster(false)}
        />
      )}
    </>
  );
});

export default Pricing;
